// Wave 7 Step 4 — admin Vendors page now writable.
// Phase 5 Migration C sub-track (b) shipped the read-only list; this wave
// adds row-click → VendorDetailPanel, "Add vendor" header button →
// VendorFormPanel (create mode), and Edit affordance → VendorFormPanel
// (edit mode). State machine mirrors Clients/index.tsx.

import { useCallback, useEffect, useState } from 'react';
import { Briefcase, Plus } from 'lucide-react';
import { palette, shadow, transition, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { useVendors, type VendorWithTotals } from './hooks';
import { MetricCard } from '../../components/common/MetricCard';
import { EmptyState } from '../../components/common/EmptyState';
import { Skeleton, TableSkeleton } from '../../components/common/Skeleton';
import { Toast } from '../../components/common/Toast';
import { VendorFormPanel } from './VendorFormPanel';
import { VendorDetailPanel } from './VendorDetailPanel';
import { supabase } from '../../lib/supabase';
import type { Vendor } from '../../types';

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

const TD: React.CSSProperties = { fontSize: 13, color: palette.g700, padding: '10px 12px', borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap', cursor: 'pointer' };
const TH: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap' };

export function VendorsPage() {
  const business = useAuthStore(s => s.business);
  const { vendors, loading, error, refetch } = useVendors(business?.id);

  // Detail = the list-row VendorWithTotals; Edit = full Vendor row (fetched on demand).
  const [selected, setSelected] = useState<VendorWithTotals | null>(null);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => { document.title = 'CrewBit · Vendors'; }, []);

  const openEditFor = useCallback(async (vendorId: string) => {
    const { data, error: err } = await supabase
      .from('vendors')
      .select('id, business_id, company_name, contact_name, email, phone, address, service_type, payment_terms, default_rate, business_number, default_tax_type, status, notes, created_at, updated_at')
      .eq('id', vendorId)
      .maybeSingle();
    if (err || !data) {
      setToast({ msg: 'Vendor not found.', type: 'error' });
      return;
    }
    setSelected(null);
    setEditingVendor(data as Vendor);
  }, []);

  const totalSpent = vendors.reduce((s: number, v: VendorWithTotals) => s + Number(v.total_spent ?? 0), 0);
  const totalOutstanding = vendors.reduce((s: number, v: VendorWithTotals) => s + Number(v.outstanding_amount ?? 0), 0);
  const outstandingBills = vendors.reduce((s: number, v: VendorWithTotals) => s + (v.outstanding_bills ?? 0), 0);

  if (loading) return (
    <div>
      <Skeleton width={100} height={24} style={{ marginBottom: 24 }} />
      <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20 }}>
        <TableSkeleton rows={5} columns={5} />
      </div>
    </div>
  );

  if (error) return (
    <div>
      <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Vendors</div>
      <div style={{ padding: '40px 0', textAlign: 'center', color: palette.red, fontSize: 13.5 }}>{error}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Vendors</div>
          <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 4 }}>Vendor entities consolidate bills and expenses under one record</div>
        </div>
        <button type="button" onClick={() => setShowCreate(true)} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '9px 16px', borderRadius: 8, border: 'none',
          background: palette.accent, color: palette.white,
          fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}>
          <Plus size={14} />
          Add vendor
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <MetricCard label="Total vendors" value={String(vendors.length)} />
        <MetricCard label="Total spent" value={fmt$(totalSpent)} />
        <MetricCard label="Outstanding bills" value={String(outstandingBills)} sub={fmt$(totalOutstanding)} />
      </div>

      <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20, boxShadow: shadow.card }}>
        {vendors.length === 0 ? (
          <EmptyState
            icon={<Briefcase size={22} />}
            title="No vendors yet"
            subtitle="Add your first vendor to start consolidating bills and expenses under one record."
            actionLabel="Add vendor"
            onAction={() => setShowCreate(true)}
          />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Vendor</th>
                <th style={TH}>Contact</th>
                <th style={{ ...TH, textAlign: 'right' }}>Total spent</th>
                <th style={{ ...TH, textAlign: 'right' }}>Outstanding</th>
                <th style={{ ...TH, textAlign: 'right' }}>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v: VendorWithTotals) => (
                <tr key={v.id} style={{ transition: `background ${transition.fast}` }}
                  onClick={() => setSelected(v)}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={TD}>
                    <div style={{ fontWeight: 600, color: admin.header }}>{v.company_name}</div>
                    {v.default_tax_type ? <div style={{ fontSize: 11, color: palette.g400, marginTop: 2 }}>Default tax · {v.default_tax_type}</div> : null}
                  </td>
                  <td style={TD}>{v.email ?? v.phone ?? '—'}</td>
                  <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(Number(v.total_spent ?? 0))}</td>
                  <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {v.outstanding_bills > 0 ? `${v.outstanding_bills} · ${fmt$(Number(v.outstanding_amount ?? 0))}` : '—'}
                  </td>
                  <td style={{ ...TD, textAlign: 'right' }}>{formatDate(v.last_transaction_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <VendorDetailPanel
        vendor={selected}
        onClose={() => setSelected(null)}
        onEdit={() => { if (selected) void openEditFor(selected.id); }}
      />

      <VendorFormPanel
        isOpen={showCreate || editingVendor !== null}
        vendor={editingVendor}
        onClose={() => { setShowCreate(false); setEditingVendor(null); }}
        onSaved={() => { refetch(); }}
        onArchived={() => { refetch(); }}
      />

      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
